import React, { useCallback } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { ALREADY_DELEGATED_MESSAGE, mapToErrorMessage } from '@components/Web3/utils'
import { SECOND_MS } from 'data/constants'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { GetSigner } from 'privy/WalletReady'
import {
    SignerUndefinedError,
    useClaimAndIncreaseStakeToTownTransaction,
    useClaimAndStakeToTownTransaction,
    useIncreaseStakeToTownTransaction,
    useStakeToTownTransaction,
} from 'use-towns-client'
import { formatUnits, isAddress } from 'viem'

export type SubmitStakeArgs = {
    amount: bigint
    beneficiary: `0x${string}`
    getSigner: GetSigner
    spaceId: `0x${string}`
    operatorAddress?: `0x${string}`
    onAlreadyDelegated: () => void
}

export type SubmitIncreaseStakeArgs = {
    depositId: bigint
    amount: bigint
    getSigner: GetSigner
}

const emptyOnAlreadyDelegated = () => {}

export const useStakeToTown = () => {
    const { stakeToTown, ...transactionRest } = useStakeToTownTransaction()

    // the address of the user
    const { data: beneficiaryAddress, isLoading: beneficiaryLoading } =
        useMyAbstractAccountAddress()

    const isReady = !beneficiaryLoading && !!beneficiaryAddress

    return {
        isReady,
        ...transactionRest,
        submitStake: useCallback(
            async (args: SubmitStakeArgs) => {
                console.log('[stake-to-town] submitStake', args)
                const { amount, getSigner, spaceId, operatorAddress, onAlreadyDelegated } = args

                const signer = await getSigner()
                if (!signer) {
                    throw new SignerUndefinedError()
                }

                if (!amount || amount === 0n) {
                    return toasts.error(
                        new Error('Amount must be greater than 0'),
                        onAlreadyDelegated,
                    )
                }

                if (!isAddress(spaceId)) {
                    return toasts.error(new Error('No valid town provided'), onAlreadyDelegated)
                }

                if (operatorAddress && !isAddress(operatorAddress as string)) {
                    return toasts.error(
                        new Error('Invalid operator address provided'),
                        onAlreadyDelegated,
                    )
                }

                if (!beneficiaryAddress) {
                    return toasts.error(new Error('No beneficiary provided'), onAlreadyDelegated)
                }

                toasts.submitting()

                try {
                    return new Promise<void>((resolve, reject) => {
                        stakeToTown(
                            {
                                signer,
                                amount,
                                delegatee: spaceId,
                                operatorAddress,
                                beneficiary: beneficiaryAddress,
                            },
                            {
                                onSuccess: () => {
                                    // Track stake success analytics
                                    Analytics.getInstance().track('stake success', {
                                        spaceID: spaceId,
                                        nodeAddress: operatorAddress || null,
                                        stakeAmount: Number(formatUnits(amount, 18)),
                                        success: true,
                                    })
                                    toasts.success()
                                    resolve()
                                },
                                onError: (error: Error) => {
                                    // Track stake failure analytics
                                    Analytics.getInstance().track('stake success', {
                                        spaceID: spaceId,
                                        nodeAddress: operatorAddress || null,
                                        stakeAmount: Number(formatUnits(amount, 18)),
                                        success: false,
                                    })
                                    toasts.error(error, onAlreadyDelegated)
                                    reject(error)
                                },
                            },
                        )
                    })
                } catch (error) {
                    console.error('Error during staking process:', error)
                    const catchedError =
                        error instanceof Error
                            ? error
                            : new Error('An unexpected error occurred during staking.')
                    toasts.error(catchedError, onAlreadyDelegated)
                }
            },
            [beneficiaryAddress, stakeToTown],
        ),
    }
}

export const useIncreaseStake = () => {
    const { increaseStakeToTown, ...transactionRest } = useIncreaseStakeToTownTransaction()

    return {
        ...transactionRest,
        submitIncreaseStake: useCallback(
            async (args: SubmitIncreaseStakeArgs) => {
                console.log('[increase-stake-to-town] submitIncreaseStake', args)
                const { depositId, amount, getSigner } = args

                if (!amount || amount === 0n) {
                    return toasts.error(
                        new Error('Amount must be greater than 0'),
                        emptyOnAlreadyDelegated,
                    )
                }
                if (!getSigner) {
                    return toasts.error(
                        new Error('Signer not available. Cannot increase stake.'),
                        emptyOnAlreadyDelegated,
                    )
                }
                if (!increaseStakeToTown) {
                    return toasts.error(
                        new Error('Increase staking is not available.'),
                        emptyOnAlreadyDelegated,
                    )
                }

                toasts.submitting()

                try {
                    const signer = await getSigner()
                    if (!signer) {
                        createPrivyNotAuthenticatedNotification()
                        return
                    }

                    return new Promise<void>((resolve, reject) => {
                        increaseStakeToTown(
                            {
                                signer,
                                depositId,
                                amount,
                            },
                            {
                                onSuccess: () => {
                                    toasts.success()
                                    resolve()
                                },
                                onError: (error: Error) => {
                                    toasts.error(error, emptyOnAlreadyDelegated)
                                    reject(error)
                                },
                            },
                        )
                    })
                } catch (error) {
                    console.error('Error during increase stake process:', error)
                    const catchedError =
                        error instanceof Error
                            ? error
                            : new Error('An unexpected error occurred during increase stake.')
                    toasts.error(catchedError, emptyOnAlreadyDelegated)
                }
            },
            [increaseStakeToTown],
        ),
    }
}

export const useClaimAndStakeToTown = () =>
    useClaimAndStakeToTownTransaction({
        onSuccess: () => toasts.success(),
        onError: (error) => toasts.error(error, emptyOnAlreadyDelegated),
        onPending: () => toasts.submitting(),
    })

export const useClaimAndIncreaseStakeToTown = () =>
    useClaimAndIncreaseStakeToTownTransaction({
        onSuccess: () => toasts.success(),
        onError: (error) => toasts.error(error, emptyOnAlreadyDelegated),
        onPending: () => toasts.submitting(),
    })

const toasts = {
    submitting: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Pending toast={toast} message="Submitting your stake..." />
            ),
            {
                id: 'stake-tx',
                duration: Number.POSITIVE_INFINITY,
            },
        )
    },

    success: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message="Successfully staked!"
                    subMessage="Your $TOWNS tokens have been staked."
                />
            ),
            { id: 'stake-tx', duration: SECOND_MS * 4 },
        )
    },

    error: (error: Error, onAlreadyDelegated: () => void) => {
        console.error(error.message)
        const mappedError = mapToErrorMessage({ error, source: 'stake-to-town' })
        if (mappedError?.includes(ALREADY_DELEGATED_MESSAGE)) {
            popupToast(
                ({ toast }) => (
                    <StandardToast.Success
                        toast={toast}
                        message="This town was just delegated. Please retry to complete your stake."
                    />
                ),
                { id: 'stake-tx', duration: SECOND_MS * 25 },
            )
            onAlreadyDelegated?.()
            return
        }
        popupToast(
            ({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Staking failed"
                    subMessage={mappedError}
                />
            ),
            { id: 'stake-tx', duration: SECOND_MS * 5 },
        )
    },
}
